import type {
  CircleLayerSpecification,
  GeoJSONSourceSpecification,
  LineLayerSpecification,
  SymbolLayerSpecification,
} from 'maplibre-gl';
import type { Feature, FeatureCollection } from 'geojson';

import type { RouteDraft } from '@/stores/RouteWorkspaceContext';

export const ROUTE_DRAFT_SOURCE_ID = 'route-draft';
export const ROUTE_DRAFT_MARKERS_LAYER_ID = 'route-draft-markers';
export const ROUTE_DRAFT_LABELS_LAYER_ID = 'route-draft-labels';
export const ROUTE_DRAFT_LINE_LAYER_ID = 'route-draft-line';

type DraftEndpointKind = 'origin' | 'destination';

interface DraftFeatureProps {
  readonly kind: DraftEndpointKind;
}

export function buildEmptyRouteDraftCollection(): FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

export function buildRouteDraftCollection(draft: RouteDraft | null): FeatureCollection {
  if (!draft) {
    return buildEmptyRouteDraftCollection();
  }
  const features: Feature[] = [];
  if (draft.origin) {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [draft.origin.lon, draft.origin.lat] },
      properties: { kind: 'origin' } satisfies DraftFeatureProps,
    });
  }
  if (draft.destination) {
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [draft.destination.lon, draft.destination.lat],
      },
      properties: { kind: 'destination' } satisfies DraftFeatureProps,
    });
  }
  if (draft.origin && draft.destination) {
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [
          [draft.origin.lon, draft.origin.lat],
          [draft.destination.lon, draft.destination.lat],
        ],
      },
      properties: {},
    });
  }
  return { type: 'FeatureCollection', features };
}

export function buildRouteDraftSource(): GeoJSONSourceSpecification {
  return {
    type: 'geojson',
    data: buildEmptyRouteDraftCollection(),
  };
}

export function buildRouteDraftLineLayer(): LineLayerSpecification {
  return {
    id: ROUTE_DRAFT_LINE_LAYER_ID,
    type: 'line',
    source: ROUTE_DRAFT_SOURCE_ID,
    filter: ['==', ['geometry-type'], 'LineString'],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#1677ff',
      'line-width': 2,
      'line-opacity': 0.7,
      'line-dasharray': [1, 2],
    },
  };
}

export function buildRouteDraftMarkersLayer(): CircleLayerSpecification {
  return {
    id: ROUTE_DRAFT_MARKERS_LAYER_ID,
    type: 'circle',
    source: ROUTE_DRAFT_SOURCE_ID,
    filter: ['==', ['geometry-type'], 'Point'],
    paint: {
      'circle-radius': 9,
      'circle-color': [
        'match', ['get', 'kind'],
        'origin', '#52c41a',
        'destination', '#fa8c16',
        '#1677ff',
      ],
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 2,
      'circle-opacity': 0.95,
    },
  };
}

export function buildRouteDraftLabelsLayer(): SymbolLayerSpecification {
  return {
    id: ROUTE_DRAFT_LABELS_LAYER_ID,
    type: 'symbol',
    source: ROUTE_DRAFT_SOURCE_ID,
    filter: ['==', ['geometry-type'], 'Point'],
    layout: {
      'text-field': [
        'match', ['get', 'kind'],
        'origin', 'A',
        'destination', 'B',
        '',
      ],
      'text-size': 13,
      'text-font': ['Noto Sans Bold'],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': '#ffffff',
      'text-halo-color': 'rgba(0, 0, 0, 0.35)',
      'text-halo-width': 1,
    },
  };
}
